import {
  AdvertiserDetailResponse,
  Api,
  BindWalletRequest,
  CampaignSquadContributeResponse,
  CampaignSquadRewardResponse,
  CampaignSquadTaskDetailResponse,
  CampaignSquadTasksResponse,
  CampaignsResponse,
  CampaignTasksResponse,
  CreateTeamRequest,
  EligiblePostRequest,
  FcmBindRequest,
  FcmBindResponse,
  HttpResponse,
  InviteBindRequest,
  InviteBindResponse,
  JoinTeamRequest,
  NotificationHistoryResponse,
  NotifySwitchResponse,
  RequestParams,
  ReversePrivacyAuth,
  SetRewardWalletRequest,
  SetRewardWalletResponse,
  UpdateNotifySwitchRequest,
  UpdateNotifySwitchResponse,
  WebResultUpdateSocialPrivacyResponse,
  UpdateTeamRequest,
  WebResultUpdateTeamResponse,
  WebResultJoinTeamResponse,
  WebResultLeaveTeamResponse,
  WebResultRemoveMemberResponse,
  WebResultCreateTeamResponse,
  UpdateMbtiRequest,
  UpdateMbtiResponse,
  CampaignInvitesResponse,
  CampaignClaimRewardResponse,
  CampaignLadderResponse,
  CampaignHatchResponse,
  HatchCampaignLevelRequest,
  CampaignRewardTiersResponse,
  CampaignLuckyDrawResponse,
  CampaignOverviewResponse,
  CampaignInviteTierStatsResponse,
  CreateOrUpdateApplicationRequest,
  CampaignApplicationResponse,
  AnalysisResultResponse,
  OnboardingConfigResponse,
  AudienceSegmentListResponse,
  MyCampaignPageResponse,
  CampaignDashboardViewResponse,
} from "./trex-api";

declare const window: (Window & typeof globalThis) | undefined;
const OS_DEVICE_ID_STORAGE_KEY = "trex2b_os_device_id";

export class TrexApiProxy {
  private client: InstanceType<typeof Api>;
  public token: string | null = null;
  public refreshToken: string | null = null;
  private isRefreshing = false;
  private refreshPromise: Promise<void> | null = null;
  private osDeviceId: string | null = null;
  private onTokenRefreshFailed?: () => void;
  private onTokenRefreshed?: (tokens: {
    accessToken: string;
    refreshToken: string;
  }) => void;
  private shouldAttemptTokenRefresh?: () => boolean;
  /** Bumped on logout or new login so in-flight refresh responses can be discarded. */
  private authSessionGeneration = 0;

  constructor(client: InstanceType<typeof Api>, baseUrl?: string) {
    this.client = client;
    if (baseUrl) {
      this.client.baseUrl = baseUrl;
    }
    this.osDeviceId = this.getOrCreateOsDeviceId();
  }

  private getOrCreateOsDeviceId(): string {
    if (typeof window === "undefined") return "";
    const storedId = window.localStorage.getItem(OS_DEVICE_ID_STORAGE_KEY);
    if (storedId) return storedId;
    const newId =
      "trex_" +
      Math.random().toString(36).substring(2) +
      Date.now().toString(36);
    window.localStorage.setItem(OS_DEVICE_ID_STORAGE_KEY, newId);
    return newId;
  }

  getHeaders(params: any) {
    const device = this.osDeviceId ? { os_device_id: this.osDeviceId } : {};
    const auth = this.token ? { Authorization: `Bearer ${this.token}` } : {};
    return {
      ...params.headers,
      ...auth,
      ...device,
    };
  }

  setToken(token: string) {
    this.token = token;
  }

  setRefreshToken(refreshToken: string) {
    this.refreshToken = refreshToken;
  }

  clearToken() {
    this.authSessionGeneration += 1;
    this.token = null;
    this.refreshToken = null;
  }

  beginAuthSession() {
    this.authSessionGeneration += 1;
  }

  setOnTokenRefreshFailed(callback: () => void) {
    this.onTokenRefreshFailed = callback;
  }

  setOnTokenRefreshed(
    callback: (tokens: { accessToken: string; refreshToken: string }) => void,
  ) {
    this.onTokenRefreshed = callback;
  }

  setShouldAttemptTokenRefresh(callback: () => boolean) {
    this.shouldAttemptTokenRefresh = callback;
  }

  private isStaleRefreshResult(
    sessionGenerationAtStart: number,
    refreshTokenSnapshot: string | null,
  ): boolean {
    return (
      sessionGenerationAtStart !== this.authSessionGeneration ||
      refreshTokenSnapshot !== this.refreshToken
    );
  }

  public async refreshAccessToken(): Promise<void> {
    if (this.isRefreshing) {
      return this.refreshPromise!;
    }
    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      const sessionGenerationAtStart = this.authSessionGeneration;
      const refreshTokenSnapshot = this.refreshToken;

      try {
        if (
          this.shouldAttemptTokenRefresh &&
          !this.shouldAttemptTokenRefresh()
        ) {
          return;
        }
        if (!refreshTokenSnapshot) {
          return;
        }

        const response = await this.client.v1.refreshAccessToken({
          accessToken: this.token || "",
          refreshToken: refreshTokenSnapshot,
        });

        if (
          this.isStaleRefreshResult(
            sessionGenerationAtStart,
            refreshTokenSnapshot,
          )
        ) {
          return;
        }
        if (
          this.shouldAttemptTokenRefresh &&
          !this.shouldAttemptTokenRefresh()
        ) {
          return;
        }

        if (response.data?.obj?.accessToken) {
          this.setToken(response.data.obj.accessToken);
          const nextRefreshToken =
            response.data.obj.refreshToken || refreshTokenSnapshot;
          if (nextRefreshToken) {
            this.setRefreshToken(nextRefreshToken);
            this.onTokenRefreshed?.({
              accessToken: response.data.obj.accessToken,
              refreshToken: nextRefreshToken,
            });
          }
        } else {
          this.clearToken();
          this.onTokenRefreshFailed?.();
        }
      } catch (error) {
        if (
          this.isStaleRefreshResult(
            sessionGenerationAtStart,
            refreshTokenSnapshot,
          )
        ) {
          return;
        }
        this.clearToken();
        this.onTokenRefreshFailed?.();
        throw error;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();
    return this.refreshPromise;
  }

  private async execute<T>(
    request: () => Promise<T>,
    requireInitialToken: boolean = true,
  ): Promise<T> {
    if (requireInitialToken && !this.token) {
      throw new Error("No token available");
    }

    try {
      return await request();
    } catch (error: any) {
      const canRefresh =
        this.shouldAttemptTokenRefresh?.() ?? Boolean(this.refreshToken);
      if (error?.status === 401 && this.refreshToken && canRefresh) {
        await this.refreshAccessToken();
        try {
          return await request();
        } catch (retryError: any) {
          if (retryError?.status === 401) {
            this.clearToken();
            this.onTokenRefreshFailed?.();
          }
          throw retryError;
        }
      }
      throw error;
    }
  }

  private async requestWithAuth<T>(request: () => Promise<T>): Promise<T> {
    return this.execute(request, true);
  }

  private async requestWithoutAuth<T>(request: () => Promise<T>): Promise<T> {
    return this.execute(request, false);
  }

  get v1() {
    return {
      ...this.client.v1,
      bindInvite: (data: InviteBindRequest, params: any = {}) =>
        this.requestWithAuth<HttpResponse<InviteBindResponse, any>>(() =>
          this.client.v1.bindInvite(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      thirdAuthorize: (data: any, params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.thirdAuthorize(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      logout: (params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.logout({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      taskList: (params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.queryBySocialInfo({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getPassportInfo: (params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.getPassportInfo({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      thirdBindings: (params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.thirdBindings({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getPassportConnect: (query: any = {}, params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.getPassportConnect(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      invite: (
        query?: {
          type?: string;
          bizId?: string;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.invite(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      bindWallet: (params: { query: BindWalletRequest; params?: any }) =>
        this.requestWithAuth(() =>
          this.client.v1.bindWallet(params.query, {
            ...params.params,
            headers: this.getHeaders(params.params || {}),
          }),
        ),
      setRewardWallet: (
        data: SetRewardWalletRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<SetRewardWalletResponse, any>>(() =>
          this.client.v1.setRewardWallet(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      setHandleName: (query: { handleName: string }, params: any = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.setHandleName(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      privacyAuth: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.privacyAuth({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      reversePrivacyAuth: (
        data: ReversePrivacyAuth,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth(() =>
          this.client.v1.reversePrivacyAuth(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),

      eligiblePost: (data: EligiblePostRequest, params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.eligiblePost(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),

      eligibleGet: (params: RequestParams = {}) =>
        this.requestWithAuth(() =>
          this.client.v1.eligibleGet({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),

      sign: (type: string, params: RequestParams = {}) =>
        this.requestWithoutAuth(() =>
          this.client.v1.sign(type, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      createTeam: (data: CreateTeamRequest, params: any = {}) =>
        this.requestWithAuth<HttpResponse<WebResultCreateTeamResponse, any>>(
          () =>
            this.client.v1.createTeam(data, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      joinTeam: (data: JoinTeamRequest, params: any = {}) =>
        this.requestWithAuth<HttpResponse<WebResultJoinTeamResponse, any>>(() =>
          this.client.v1.joinTeam(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      removeTeamMember: (userId: string[], params: any = {}) =>
        this.requestWithAuth<HttpResponse<WebResultRemoveMemberResponse, any>>(
          () =>
            this.client.v1.removeTeamMember(userId, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      leaveTeam: (params: any = {}) =>
        this.requestWithAuth<HttpResponse<WebResultLeaveTeamResponse, any>>(
          () =>
            this.client.v1.leaveTeam({
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getPassportTeamCode: (
        query: {
          teamId?: string;
          code?: string;
          scene?: string;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithoutAuth(() =>
          this.client.v1.getPassportTeamCode(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      queryPersonas: (
        query: {
          parentCode: string;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithoutAuth(() =>
          this.client.v1.queryPersonas(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getPersonasStats: (params: RequestParams = {}) =>
        this.requestWithoutAuth(() =>
          this.client.v1.personaStats({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      updateTeamType: ({ targetType }: { targetType: "public" | "private" }) =>
        this.requestWithAuth(() =>
          this.client.v1.updateTeamType(
            { targetType },
            { headers: this.getHeaders({}) },
          ),
        ),
      getCampaigns: (params: RequestParams = {}) =>
        this.requestWithoutAuth<HttpResponse<CampaignsResponse, any>>(() =>
          this.client.v1.getCampaigns({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getAdvertiserDetail: (
        campaignAdvertiserId: string,
        params: RequestParams = {},
      ) =>
        this.requestWithoutAuth<HttpResponse<AdvertiserDetailResponse, any>>(
          () =>
            this.client.v1.getAdvertiserDetail(campaignAdvertiserId, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getCampaignReward: (campaignId: string, params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<CampaignClaimRewardResponse, any>>(
          () =>
            this.client.v1.getCampaignReward(campaignId, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      fcmBind: (data: FcmBindRequest, params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<FcmBindResponse, any>>(() =>
          this.client.v1.fcmBind(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      queryNotifySwitch: (params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<NotifySwitchResponse, any>>(() =>
          this.client.v1.queryNotifySwitch({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      updateNotifySwitch: (
        data: UpdateNotifySwitchRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<UpdateNotifySwitchResponse, any>>(
          () =>
            this.client.v1.updateNotifySwitch(data, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getNotificationHistory: (params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<NotificationHistoryResponse, any>>(
          () =>
            this.client.v1.getNotificationHistory({
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      updateSocialPrivacy: (
        query: {
          socialPrivacy: string;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<
          HttpResponse<WebResultUpdateSocialPrivacyResponse, any>
        >(() =>
          this.client.v1.updateSocialPrivacy(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      personaFeature: (params: RequestParams = {}) =>
        this.requestWithoutAuth(() =>
          this.client.v1.personaFeature({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignTasks: (
        campaignAdvertiserId: string,
        parentCode: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignTasksResponse, any>>(() =>
          this.client.v1.getCampaignTasks(campaignAdvertiserId, parentCode, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      updateTeam: (data: UpdateTeamRequest, params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<WebResultUpdateTeamResponse, any>>(
          () =>
            this.client.v1.updateTeam(data, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getCampaignSquadTasks: (
        campaignAdvertiserId: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignSquadTasksResponse, any>>(
          () =>
            this.client.v1.getCampaignSquadTasks(campaignAdvertiserId, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getCampaignSquadTaskDetail: (
        campaignAdvertiserId: string,
        code: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<
          HttpResponse<CampaignSquadTaskDetailResponse, any>
        >(() =>
          this.client.v1.getCampaignSquadTaskDetail(
            campaignAdvertiserId,
            code,
            {
              ...params,
              headers: this.getHeaders(params),
            },
          ),
        ),
      contributeCampaignSquadTask: (
        campaignAdvertiserId: string,
        code: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<
          HttpResponse<CampaignSquadContributeResponse, any>
        >(() =>
          this.client.v1.contributeCampaignSquadTask(
            campaignAdvertiserId,
            code,
            {
              ...params,
              headers: this.getHeaders(params),
            },
          ),
        ),
      claimCampaignSquadReward: (
        campaignAdvertiserId: string,
        code: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignSquadRewardResponse, any>>(
          () =>
            this.client.v1.claimCampaignSquadReward(
              campaignAdvertiserId,
              code,
              {
                ...params,
                headers: this.getHeaders(params),
              },
            ),
        ),
      updateMbti: (data: UpdateMbtiRequest, params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<UpdateMbtiResponse, any>>(() =>
          this.client.v1.updateMbti(data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignInvites: (
        campaignAdvertiserId: string,
        query?: {
          nextToken?: string;
          limit?: number;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignInvitesResponse, any>>(() =>
          this.client.v1.getCampaignInvites(campaignAdvertiserId, query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignLadder: (
        campaignAdvertiserId: string,
        query?: {
          nextToken?: string;
          limit?: number;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignLadderResponse, any>>(() =>
          this.client.v1.getCampaignLadder(campaignAdvertiserId, query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignRewardTiers: (
        campaignAdvertiserId: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignRewardTiersResponse, any>>(
          () =>
            this.client.v1.getCampaignRewardTiers(campaignAdvertiserId, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getActiveCampaigns: (params: RequestParams = {}) =>
        this.requestWithoutAuth<HttpResponse<AdvertiserDetailResponse, any>>(
          () =>
            this.client.v1.getActiveCampaigns({
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      hatchCampaignLevel: (
        campaignAdvertiserId: string,
        data: HatchCampaignLevelRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignHatchResponse, any>>(() =>
          this.client.v1.hatchCampaignLevel(campaignAdvertiserId, data, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignLuckyDraw: (
        campaignAdvertiserId: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignLuckyDrawResponse, any>>(() =>
          this.client.v1.getCampaignLuckyDraw(campaignAdvertiserId, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignOverview: (
        campaignAdvertiserId: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignOverviewResponse, any>>(() =>
          this.client.v1.getCampaignOverview(campaignAdvertiserId, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignInviteTierStats: (
        campaignAdvertiserId: string,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<
          HttpResponse<CampaignInviteTierStatsResponse, any>
        >(() =>
          this.client.v1.getCampaignInviteTierStats(campaignAdvertiserId, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      createApplication: (
        data: CreateOrUpdateApplicationRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignApplicationResponse, any>>(
          () =>
            this.client.v1.createApplication(data, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      updateApplication: (
        id: string,
        data: CreateOrUpdateApplicationRequest,
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignApplicationResponse, any>>(
          () =>
            this.client.v1.updateApplication(id, data, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getAdvertiserIntelligence: (
        query: {
          input: string;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<AnalysisResultResponse, any>>(() =>
          this.client.v1.getAdvertiserIntelligence(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      listAudienceSegments: (params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<AudienceSegmentListResponse, any>>(
          () =>
            this.client.v1.listAudienceSegments({
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      getOnboardingConfigs: (params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<OnboardingConfigResponse, any>>(() =>
          this.client.v1.getOnboardingConfigs({
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getApplicationById: (id: string, params: RequestParams = {}) =>
        this.requestWithAuth<HttpResponse<CampaignApplicationResponse, any>>(
          () =>
            this.client.v1.getApplicationById(id, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
      listMyCampaigns: (
        query?: {
          statusFilter?: string[];
          page?: number;
          size?: number;
        },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<MyCampaignPageResponse, any>>(() =>
          this.client.v1.listMyCampaigns(query, {
            ...params,
            headers: this.getHeaders(params),
          }),
        ),
      getCampaignDashboard: (
        campaignId: string,
        query?: { timeWindow?: string },
        params: RequestParams = {},
      ) =>
        this.requestWithAuth<HttpResponse<CampaignDashboardViewResponse, any>>(
          () =>
            this.client.v1.getCampaignDashboard(campaignId, query, {
              ...params,
              headers: this.getHeaders(params),
            }),
        ),
    };
  }
}
